/**
 * WelcomeSplash — compact "hello" card that plays on the first login of
 * the day. V2 redesign 2026-02-26 (user ask: "since the V2 launch the
 * splash screen is too much its taking too much of the screen since we
 * moved into a popups can we convert the splash screen to be better?").
 *
 * Old design (V1, deprecated): fixed inset-0 full-screen gradient +
 * 8xl emoji + 6xl headline — a big cinematic moment but visually
 * overwhelming against the new Dialog-based pop-out language.
 *
 * New design (V2): compact centered card, ~380px wide, subtle blurred
 * backdrop, gradient reserved to a slim header strip inside the card.
 * Matches the aesthetic of the Wow Crew / Leaderboards / Contest
 * modals so login now feels like "just another pop-out" instead of a
 * cinematic takeover. Auto-dismiss trimmed from 2.6s → 1.8s since the
 * new card is easier to parse at a glance.
 *
 * Behavior unchanged:
 *   - Fires a single confetti burst on mount.
 *   - Tap the card OR the backdrop to dismiss early.
 *   - Show frequency still gated in App.js (1× per crew_id per MST day).
 *   - Time-of-day + Spider-Man theme swap logic preserved.
 *   - Personalized stat line still driven off yesterday's /api/tally.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { useTheme } from "../ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spider-Man override — replaces the time-of-day palette entirely when
// the Spider-Man theme is active. Detected via the primary+accent
// color signature (same recipe App.js uses for the theme overlay).
const SPIDER_CONFIG = {
  art: "🕷️",
  label: "Spidey senses tingling",
  gradient: "linear-gradient(135deg, #7F1D1D 0%, #DC2626 55%, #1E3A8A 100%)",
  lines: [
    "Thwip! Let's go, {name}!",
    "Great power, great emails, {name}!",
    "Suit up, {name}!",
    "With great responsibility comes great goals, {name}!",
    "Time to swing into action, {name}!",
  ],
};

// Pitch Black — Pokémon Mega Evolution event override. Uses the event
// artwork in the header strip instead of a time-of-day emoji.
const PITCH_BLACK_CONFIG = {
  image: "/darkrai.webp",
  label: "Pitch Black event",
  gradient: "linear-gradient(135deg, #050505 0%, #1a0320 55%, #050505 100%)",
  lines: [
    "Let's go, {name} ⚡",
    "Pitch Black mode on, {name}!",
    "Mega energy today, {name}!",
    "Light up the store, {name} ⚡",
    "Big drop day, {name}. Have fun!",
  ],
};

// Time-of-day config table. Each entry drives BOTH the header strip
// gradient inside the card and the pool of greeting lines.
const TOD_CONFIG = {
  morning: {
    art: "🌅",
    label: "Good morning",
    gradient: "linear-gradient(135deg, #F97316 0%, #FBBF24 55%, #FDE68A 100%)",
    lines: [
      "Rise and grind, {name}!",
      "Fresh page, {name}!",
      "Let's make it happen, {name}!",
      "Here comes the sun, {name}!",
    ],
  },
  afternoon: {
    art: "☀️",
    label: "Good afternoon",
    gradient: "linear-gradient(135deg, #38BDF8 0%, #0EA5E9 55%, #0066FF 100%)",
    lines: [
      "What's up, {name}!",
      "Back at it, {name}!",
      "Let's crush it, {name}!",
      "Big day, {name}!",
    ],
  },
  evening: {
    art: "🌇",
    label: "Good evening",
    gradient: "linear-gradient(135deg, #F472B6 0%, #A855F7 55%, #4C1D95 100%)",
    lines: [
      "Home stretch, {name}!",
      "Closing hero, {name}!",
      "You showed up, {name}!",
      "One more push, {name}!",
    ],
  },
  night: {
    art: "🌙",
    label: "Working late?",
    gradient: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 55%, #312E81 100%)",
    lines: [
      "Late shift legend, {name}!",
      "Owl mode, {name}!",
      "Burning the midnight oil, {name}!",
      "Grinding late, {name}!",
    ],
  },
};

const getTodKey = () => {
  const h = parseInt(
    new Intl.DateTimeFormat("en-US", { timeZone: "America/Denver", hour: "2-digit", hour12: false })
      .format(new Date())
      .replace(/\D/g, ""),
    10,
  );
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
};

// Yesterday's MST date string for the personalized stat line.
const getYesterdayMst = () => {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return yesterday.toLocaleDateString("en-CA", { timeZone: "America/Denver" });
};

const rankLabel = (rank, total) => {
  if (rank === 1) return `#1 in the store`;
  if (rank === 2) return `#2 in the store`;
  if (rank === 3) return `top 3`;
  if (rank <= 5 && total >= 5) return `top 5`;
  return `nice work`;
};

export default function WelcomeSplash({ user, onDismiss }) {
  const [statLine, setStatLine] = useState(null);
  const { settings: themeSettings } = useTheme();
  const isSpider = (() => {
    if (!themeSettings) return false;
    const p = (themeSettings.primary_color || "").toLowerCase();
    const a = (themeSettings.accent_color || "").toLowerCase();
    return p === "#dc2626" && a === "#1e40af";
  })();
  const isPitchBlack = (() => {
    if (!themeSettings) return false;
    const p = (themeSettings.primary_color || "").toLowerCase();
    const a = (themeSettings.accent_color || "").toLowerCase();
    return p === "#ec008c" && a === "#a3e635";
  })();
  const todKey = getTodKey();
  const cfg = isPitchBlack ? PITCH_BLACK_CONFIG : isSpider ? SPIDER_CONFIG : TOD_CONFIG[todKey];
  const [greetIdx] = useState(() => Math.floor(Math.random() * cfg.lines.length));
  const greeting = cfg.lines[greetIdx].replace("{name}", user?.first_name || "crew");

  // Pitch Black cinematic sequence phases: greet → recall (into ball) →
  // wobble → zoom (fly away). Only advances when the theme is active.
  const [phase, setPhase] = useState("ballIn");
  const [artReady, setArtReady] = useState(false);

  // Fetch yesterday's tally to build the brag line. Best-effort — any
  // failure just falls through to a generic hype line.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const isManager = !!user?.is_manager;
      try {
        const r = await axios.get(`${API}/tally/${getYesterdayMst()}`);
        if (cancelled) return;
        if (isManager) {
          const rate = r.data?.capture_rate;
          if (typeof rate === "number" && rate > 0) {
            const pct = Math.round(rate * 100);
            const atGoal = pct >= Math.round((r.data?.goal_rate || 0.35) * 100);
            setStatLine(
              atGoal
                ? `Store hit ${pct}% yesterday — nailed goal!`
                : `Store finished at ${pct}% yesterday. Let's bump it today.`,
            );
          } else {
            setStatLine("Every email today writes the story ✨");
          }
          return;
        }
        const counts = r.data?.crew_counts || {};
        const mine = counts[user?.crew_id] || 0;
        if (mine === 0) {
          setStatLine("Fresh page, fresh start ✨");
          return;
        }
        const sorted = Object.values(counts).filter((n) => n > 0).sort((a, b) => b - a);
        const rank = sorted.findIndex((v) => v === mine) + 1;
        setStatLine(
          `You captured ${mine} email${mine === 1 ? "" : "s"} yesterday — ${rankLabel(rank, sorted.length)}!`,
        );
      } catch {
        if (!cancelled) setStatLine("Every email today writes the story ✨");
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.crew_id, user?.is_manager]);

  // Confetti + auto-dismiss on mount. Softer burst than V1 since the
  // card is smaller — 30 particles instead of 60, single center pop.
  useEffect(() => {
    try {
      confetti({
        particleCount: isPitchBlack ? 70 : 40,
        spread: isPitchBlack ? 90 : 55,
        origin: { y: 0.35 },
        ticks: 160,
        startVelocity: 34,
        colors: isPitchBlack ? ["#EC008C", "#A3E635", "#FDE047", "#22D3EE", "#A855F7"] : undefined,
      });
    } catch { /* canvas missing — skip */ }
    // Pitch Black plays a ~4s recall→zoom cinematic (dismiss handled in
    // the art-gated sequence effect below); other themes use the quick
    // 1.8s compact card.
    if (!isPitchBlack) {
      const t = setTimeout(() => onDismiss && onDismiss(), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [onDismiss, isPitchBlack]);

  // Preload Darkrai so the cinematic never starts before the character
  // is actually on screen (fixes "only the Poké Ball showed" on slower
  // connections). Falls back after 1.6s so a slow/failed image can't
  // hang the splash.
  useEffect(() => {
    if (!isPitchBlack) return undefined;
    let done = false;
    const ready = () => { if (!done) { done = true; setArtReady(true); } };
    const img = new Image();
    img.onload = ready;
    img.onerror = ready;
    img.src = "/darkrai.webp";
    if (img.complete) ready();
    const fb = setTimeout(ready, 1600);
    return () => clearTimeout(fb);
  }, [isPitchBlack]);

  // Pitch Black sequence timers — start ONLY once the art is ready so
  // Darkrai gets its full float-in moment before being recalled.
  useEffect(() => {
    if (!isPitchBlack || !artReady) return undefined;
    const timers = [
      setTimeout(() => setPhase("open"), 850),
      setTimeout(() => setPhase("reveal"), 1400),
      setTimeout(() => setPhase("charge"), 3050),
      setTimeout(() => setPhase("warp"), 3850),
      setTimeout(() => onDismiss && onDismiss(), 4500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [isPitchBlack, artReady, onDismiss]);

  // ── Pitch Black cinematic welcome ──────────────────────────────────
  if (isPitchBlack) {
    // Phases: ballIn → open → reveal → charge → warp
    const ballStyle =
      phase === "ballIn"
        ? { animation: "pbwBallDrop 620ms cubic-bezier(0.34,1.56,0.64,1) forwards" }
        : phase === "open"
        ? { animation: "pbwBallOpen 540ms ease-in forwards" }
        : { opacity: 0 };
    const chargeExtras =
      phase === "charge"
        ? { transformOrigin: "50% 50%", maskImage: "none", WebkitMaskImage: "none" }
        : {};
    const darkraiStyle =
      phase === "reveal"
        ? { animation: "pbwEmerge 900ms cubic-bezier(0.16,1,0.3,1) forwards, pbwFloat 2.6s ease-in-out 900ms infinite" }
        : phase === "charge"
        ? { animation: "pbwCharge 800ms cubic-bezier(0.5,0,0.75,0) forwards", ...chargeExtras }
        : { opacity: 0, transform: "translateX(-50%) translateY(120px) scale(0.1)" };
    const showDarkrai = artReady && (phase === "reveal" || phase === "charge");
    const capLine =
      phase === "ballIn" ? "A wild Darkrai appears…"
      : phase === "open" ? "Gotcha! ⚡"
      : phase === "charge" ? "Here it comes!"
      : phase === "warp" ? "See you out there!"
      : greeting;

    return (
      <div
        className="fixed inset-0 z-[120] flex flex-col items-center justify-center px-6 cursor-pointer overflow-hidden"
        style={{
          background: "radial-gradient(1200px 700px at 50% 30%, #1a0320 0%, #050505 70%)",
          animation: "welcomeBackdropIn 220ms ease-out forwards",
        }}
        onClick={() => onDismiss && onDismiss()}
        role="button"
        tabIndex={-1}
        aria-label="Dismiss welcome"
        data-testid="welcome-splash"
      >
        {/* neon blooms */}
        <div className="absolute -top-24 -left-16 w-[420px] h-[420px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(236,0,140,0.22), transparent 65%)", filter: "blur(6px)" }} aria-hidden />
        <div className="absolute -bottom-24 -right-16 w-[420px] h-[420px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(163,230,53,0.16), transparent 65%)", filter: "blur(6px)" }} aria-hidden />

        {/* stage */}
        <div className="relative" style={{ width: 300, height: 380 }} data-testid="welcome-splash-art">
          {showDarkrai && (
            <img
              src="/darkrai.webp"
              alt=""
              className="absolute left-1/2 -translate-x-1/2 select-none"
              style={{
                top: 0, height: 300, width: "auto", transformOrigin: "50% 100%",
                maskImage: "radial-gradient(62% 60% at 50% 42%, #000 58%, transparent 100%)",
                WebkitMaskImage: "radial-gradient(62% 60% at 50% 42%, #000 58%, transparent 100%)",
                ...darkraiStyle,
              }}
              aria-hidden
            />
          )}
          {/* white/pink release burst as the ball pops open */}
          {(phase === "open" || phase === "reveal") && (
            <div className="absolute left-1/2 rounded-full pointer-events-none"
              style={{ bottom: -30, width: 280, height: 280, transform: "translateX(-50%)",
                background: "radial-gradient(circle, rgba(255,255,255,0.95), rgba(236,0,140,0.55) 42%, transparent 72%)",
                animation: "pbwReleaseFlash 750ms ease-out forwards" }} aria-hidden />
          )}
          {/* Poké Ball — only before it opens */}
          {(phase === "ballIn" || phase === "open") && (
            <div
              className="absolute left-1/2"
              style={{ bottom: 60, width: 92, height: 92, transform: "translateX(-50%)", transformOrigin: "50% 50%", ...ballStyle }}
            >
              <div className="pbw-ball">
                <span className="pbw-ball-top" />
                <span className="pbw-ball-bottom" />
                <span className="pbw-ball-band" />
                <span className="pbw-ball-btn" />
              </div>
            </div>
          )}
        </div>
        {/* full-screen warp flash — the "fly into the home screen" beat */}
        {phase === "warp" && (
          <div className="fixed inset-0 pointer-events-none" style={{
            background: "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.98), rgba(236,0,140,0.6) 45%, rgba(5,5,5,0) 80%)",
            animation: "pbwWarp 720ms ease-in forwards" }} aria-hidden />
        )}

        <p
          className="mt-2 text-2xl sm:text-3xl font-black text-center transition-opacity duration-300"
          style={{
            fontFamily: "'Five Below Gothic', Chivo, sans-serif",
            backgroundImage: "linear-gradient(90deg, #EC008C, #FDE047, #A3E635)",
            WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
            filter: "drop-shadow(0 0 12px rgba(236,0,140,0.5))",
            opacity: phase === "zoom" ? 0.9 : 1,
          }}
          data-testid="welcome-splash-greeting"
        >
          {capLine}
        </p>
        {statLine && phase === "reveal" && (
          <p className="mt-2 text-sm font-medium text-white/70 text-center" data-testid="welcome-splash-stat">{statLine}</p>
        )}
        <p className="mt-4 text-[9px] uppercase tracking-[0.26em] font-bold text-white/40">tap to skip</p>

        <style>{`
          @keyframes welcomeBackdropIn { from { opacity: 0 } to { opacity: 1 } }
          @keyframes pbwFloat {
            0%,100% { transform: translateX(-50%) translateY(0) scale(1); }
            50%     { transform: translateX(-50%) translateY(-10px) scale(1.01); }
          }
          @keyframes pbwBallDrop {
            0%   { opacity: 0; transform: translateX(-50%) translateY(-240px) scale(0.6); }
            62%  { opacity: 1; transform: translateX(-50%) translateY(14px) scale(1.08); }
            100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          }
          @keyframes pbwBallOpen {
            0%   { transform: translateX(-50%) scale(1) rotate(0deg); filter: none; }
            22%  { transform: translateX(-50%) scale(1.16) rotate(-8deg); }
            48%  { transform: translateX(-50%) scale(1.16) rotate(8deg); filter: brightness(2); }
            100% { opacity: 0; transform: translateX(-50%) scale(1.7); filter: brightness(5) saturate(0); }
          }
          @keyframes pbwReleaseFlash {
            0%   { opacity: 0; transform: translateX(-50%) scale(0.3); }
            30%  { opacity: 1; }
            100% { opacity: 0; transform: translateX(-50%) scale(1.4); }
          }
          @keyframes pbwEmerge {
            0%   { opacity: 0; transform: translateX(-50%) translateY(150px) scale(0.14); filter: brightness(2.6) saturate(0); }
            45%  { opacity: 1; filter: brightness(1.25); }
            100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); filter: none; }
          }
          @keyframes pbwCharge {
            0%   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1) rotate(0deg); }
            55%  { opacity: 1; transform: translateX(-50%) translateY(-4%) scale(3.6) rotate(-4deg); filter: drop-shadow(0 0 40px rgba(236,0,140,0.85)); }
            100% { opacity: 0; transform: translateX(-50%) translateY(-6%) scale(7) rotate(-7deg); filter: brightness(1.7); }
          }
          @keyframes pbwWarp {
            0%   { opacity: 0; transform: scale(0.2); }
            40%  { opacity: 1; }
            100% { opacity: 0; transform: scale(3.4); }
          }
          .pbw-ball { position: relative; width: 100%; height: 100%; border-radius: 50%; overflow: hidden;
            box-shadow: 0 8px 26px rgba(0,0,0,0.6), 0 0 22px rgba(236,0,140,0.5); }
          .pbw-ball-top { position: absolute; left: 0; right: 0; top: 0; bottom: 50%; background: linear-gradient(180deg,#ff3355,#d10f2f); }
          .pbw-ball-bottom { position: absolute; left: 0; right: 0; top: 50%; bottom: 0; background: linear-gradient(180deg,#f7f7f7,#d6d6d6); }
          .pbw-ball-band { position: absolute; left: 0; right: 0; top: calc(50% - 5px); height: 10px; background: #131313; }
          .pbw-ball-btn { position: absolute; top: 50%; left: 50%; width: 28%; height: 28%; transform: translate(-50%,-50%);
            background: #fff; border: 4px solid #131313; border-radius: 50%; box-shadow: 0 0 10px rgba(255,255,255,0.9); }
          @media (prefers-reduced-motion: reduce) {
            .pbw-ball, [data-testid="welcome-splash-art"] img { animation: none !important; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 cursor-pointer"
      style={{
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        animation: "welcomeBackdropIn 220ms ease-out forwards",
      }}
      onClick={() => onDismiss && onDismiss()}
      role="button"
      tabIndex={-1}
      aria-label="Dismiss welcome splash"
      data-testid="welcome-splash"
    >
      <div
        className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl bg-white"
        style={{
          animation: "welcomeCardIn 280ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
        onClick={(e) => { e.stopPropagation(); onDismiss && onDismiss(); }}
      >
        {/* Header strip — the only place the time-of-day gradient
            shows up now. Compact enough to feel like an accent, not a
            takeover. Contains the emoji + time-of-day label. */}
        <div
          className="relative px-6 pt-6 pb-4 text-center"
          style={{ background: cfg.gradient }}
        >
          {cfg.image ? (
            <img
              src={cfg.image}
              alt=""
              className="w-full h-32 object-contain rounded-xl mb-2 select-none"
              style={{ animation: "welcomeArtFloat 2.6s ease-in-out infinite" }}
              aria-hidden
              data-testid="welcome-splash-art"
            />
          ) : (
            <div
              className="text-5xl mb-1.5 select-none inline-block"
              style={{
                filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.25))",
                animation: "welcomeArtFloat 2.6s ease-in-out infinite",
              }}
              aria-hidden
              data-testid="welcome-splash-art"
            >
              {cfg.art}
            </div>
          )}
          <p
            className="text-[10px] uppercase tracking-[0.28em] font-black text-white/90"
            data-testid="welcome-splash-tod"
          >
            {cfg.label}
          </p>
        </div>

        {/* Greeting + stat line — white card body so the copy reads
            crisply regardless of the header gradient. */}
        <div className="px-6 py-5 text-center">
          <h1
            className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight"
            style={{
              fontFamily: "'Five Below Gothic', Chivo, sans-serif",
              letterSpacing: "-0.01em",
            }}
            data-testid="welcome-splash-greeting"
          >
            {greeting}
          </h1>

          {statLine && (
            <p
              className="mt-2 text-sm font-medium text-slate-600"
              data-testid="welcome-splash-stat"
            >
              {statLine}
            </p>
          )}

          <p className="mt-4 text-[9px] uppercase tracking-[0.26em] font-bold text-slate-400">
            tap to continue
          </p>
        </div>
      </div>

      <style>{`
        @keyframes welcomeBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes welcomeCardIn {
          from { opacity: 0; transform: scale(0.9) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes welcomeArtFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
